import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { getCompanyList } from "../sharedCode/queries";
import { lookupOptions } from "../GQL/lookupOptions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { Company } from "../sharedCode/avroGraphql";
import { CompanyExtended, ExtendCompany } from "../Utils/TypeExtensions";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { TicketDatabase } from "../Utils/TicketDatabase";

export default function useCompanyBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getCompanyList, lookupOptions);

    const fetchData = (initialRun: boolean) => {
        if (initialRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getClients.slice() as Company[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: Company[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((item: Company) => ExtendCompany(item, rawList))) as CompanyExtended[];
        listToSave.sort((a, b) => {
            return a.selectName.localeCompare(b.selectName);
        });

        TicketDatabase.transaction("rw", TicketDatabase.company, async () => {
            await TicketDatabase.company
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.company.bulkDelete(keys);
                })
                .then(() => TicketDatabase.company.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getClients.slice() as Company[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.company.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
